<template>
<md-sidenav class="md-left export-nav-wrapper" ref="printNav">
    <md-toolbar class="md-dense main-toolbar">
        <md-button class="md-icon-button" @click="back()">
            <md-icon>menu</md-icon>
        </md-button>
        <h3 class="md-title">Export</h3>
    </md-toolbar>
            
    <md-layout md-flex="90" md-flex-offset="5">
        <div class="option-box">
            <h2 class="md-title">Print:</h2>
            <div class="chrome-content">
                <span class="option-title">Choose your format to print or save your project as a PDF file with all visible layers and text.</span>
                <span v-if="hideStructureMarks" class="option-note">You have structure marks hidden on your script, so they will not appear in the printed PDF.</span>
                <div class="option-content">
                    <div class="option-content-block">
                        <md-button class="md-raised md-primary" @click="openPrint(false)">Portrait</md-button>
                        <br/>
                        <span>This option will only print the PDF and any cues and notes you have located on that document. Cues located in the margins will not be visible.</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="option-box">
            <h2 class="md-title">Export:</h2>
            <span class="option-title">Download CSV file with the following information:</span>
            <div class="option-content">
                <md-switch v-model="allLayers" class="md-primary">All Layers</md-switch>
            </div>
            <div class="option-content">
                <md-checkbox v-model="layer.selected" v-for="layer in layers" :key="layer.id" v-if="!allLayers">{{layer.title}}</md-checkbox>
            </div>
            <div class="option-action">
                <md-button class="md-raised md-primary" @click="exportProject">Export</md-button>
            </div>
        </div>
    </md-layout>
</md-sidenav>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBus from '@/buses/nav';
import Utils from '@/lib/Utils';
import { pageForExport } from '@/lib/cue-page';

const PrintNav = {
    name: 'CoreHeader',
    props: [],
    data: function() {
        return {
            allLayers: true,
            allNotes: false,
            allDescriptions: false,
            cueAttributes: Utils.cueAttributes,
            attributes: { ...Object.keys(Utils.cueAttributes).reduce((attrs, attr) => ({...attrs, [attr]: true}), {})}
        };
    },
    computed: {
        ...mapGetters([
            'project',
            'hideStructureMarks',
            'userProjectByUidData',
            'layerByCue',
            'visibleListLayers'
        ]),
        layers: function() {
            return Object.values(this.project.layers || []).map(layer => ({...layer, selected: true}));
        },
        columnsAvailability: function() {
            return {
                ...Object.keys(this.attributes).reduce((attrs, attr) => ({...attrs, [attr]: this.visibleListLayers.some(layer => layer.attributes[attr])}), {}),
                workNote: true,
                label: true,
                user1: true,
                user2: true,
                user3: true,
                user4: true
            };
        },
        availableColumns: function() {
            const columns = Object.keys(this.columnsAvailability).filter(attr => this.columnsAvailability[attr]);
            const partsIndex = columns.indexOf('parts');
            if (partsIndex > -1) {
                columns.splice(partsIndex + 1, 0, 'parts_id', 'parts_label');
            }
            return columns;
        },
        layerUserDefined() {
            return this.visibleListLayers.map((layer) => {
                const userDefinedObj = {};
                
                for (const [key, value] of Object.entries(layer.userDefined || {})) {
                    if (key && value.value === true) {
                        if (value.label === '') {
                            userDefinedObj[key] = `${layer.title}: ${(key.charAt(0).toUpperCase() + key.slice(1))}`;
                        } else {
                            userDefinedObj[key] = `${layer.title}: ${value.label}`;
                        }
                    }
                }

                return {
                    id: layer.id,
                    userDefined: userDefinedObj
                };
            });
        }
    },
    methods: {
        isChrome() {
            return !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
        },
        closePrintNav() {
            this.$refs.printNav.close();
        },
        back() {
            this.closePrintNav();
            NavBus.$emit('main-nav.open');
        },
        getUserDefined(id) {
            const layers = this.userProjectByUidData.layers;
            if (layers && layers[id]) {
                if (layers[id].userDefined) {
                    return {
                        ...layers[id].userDefined
                    };
                }
            }
            return {
                user1: { label: 'User 1', value: false },
                user2: { label: 'User 2', value: false },
                user3: { label: 'User 3', value: false },
                user4: { label: 'User 4', value: false }
            };
        },
        print() {
            this.$router.replace(`/project/${this.project.id}/print`);
        },
        getAllUserDefinedKeys() {
            const keys = new Set();
            for (const layer of this.layerUserDefined) {
                for (const key of Object.keys(layer.userDefined)) {
                    keys.add(layer.userDefined[key]);
                }
            }
            return Array.from(keys);
        },
        exportProject() {
            const cues = Object.values(this.project.cues).concat().map(cue => ({...cue, layerTitle: this.layerByCue(cue).title})).sort((a, b) => {
                if (a.layerTitle < b.layerTitle) return -1;
                if (a.layerTitle > b.layerTitle) return 1;
                return 0;
            });

            const reminingColumn = this.availableColumns.map((attr) => { 
                if (!['user1', 'user2', 'user3', 'user4'].includes(attr)) { 
                    return this.cueAttributes[attr]; 
                }
                return '';
            });
            
            const allColumns = [...reminingColumn, ...this.getAllUserDefinedKeys()];
            const mergedColumns = Array.from(new Set(allColumns));
            const filteredArray = mergedColumns.filter(item => item !== '');
            
            let csv = ['Page Number', 'Layer Title', 'Cue Number', ...filteredArray].join(',') + '\n';

            for (const cue of cues.filter(c => (this.layers.find(layer => layer.id === c.layer) || {}).selected)) {
                const layer = this.layerByCue(cue);

                const reminingData = this.availableColumns.map((attr) => {
                    if (attr === 'workNote' && Array.isArray(cue.data[attr])) {
                        return '"' + cue.data[attr].filter(item => !item.archived).map(item => item.note).join('; ').replaceAll('"', '""') + '"';
                    }
                    if (['user1', 'user2', 'user3', 'user4'].includes(attr)) {
                        return '';
                    }
                    if (attr === 'parts') {
                        return '"' + cue.data.parts.toString().replaceAll('"', '""') + '"';
                    }
                    if (cue.data.parts && cue.data.partsData.length > 0 && attr === 'parts_id') {
                        return '"' + cue.data.partsData[0].id.toString().replaceAll('"', '""') + '"';
                    }
                    if (cue.data.parts && cue.data.partsData.length > 0 && (attr === 'parts_label' || cue.data.partsData[0][attr] === 'label')) {
                        return '"' + cue.data.partsData[0].label.toString().replaceAll('"', '""') + '"';
                    }
                    if (cue.data.parts && cue.data.partsData[0][attr] && attr !== 'label') {
                        return '"' + cue.data.partsData[0][attr].toString().replaceAll('"', '""') + '"';
                    }
                    if (attr === 'followHang') {
                        const enabled = cue.data && layer.attributes && layer.attributes.followHang;
                        const type = (enabled && cue.data.followHangType) || '';
                        const text = (enabled && cue.data.followHang) || '';
                        const followHangValue = type + text;
                        return '"' + followHangValue.toString().replaceAll('"', '""') + '"';
                    }
                    const value = (cue.data && layer.attributes && (layer.attributes[attr] || attr === 'label') && cue.data[attr]) || '';
                    return '"' + value.toString().replaceAll('"', '""') + '"';
                });
                const userDefinedData = this.layerUserDefined.flatMap((layark) => {
                    return Object.entries(layark.userDefined).map(([attr]) => {
                        let value = '-';
                        if (layark.id === layer.id) {
                            if (cue.data && cue.data[attr]) {
                                value = cue.data[attr];
                            }
                            return '"' + value.toString().replaceAll('"', '""') + '"';
                        }
                        return '"' + value.toString().replaceAll('"', '""') + '"';
                    });
                });
                const allData = [...reminingData, ...userDefinedData];
                const filteredData = allData.filter(item => item !== '');
                // Same order as the header: page, layer, cue. The page is the printed
                // label, as List view shows it (reported 11 Oct 2026).
                csv += [
                    '"' + pageForExport(this.project.pdfPageNumbers, cue.pageNumber).replaceAll('"', '""') + '"',
                    '"' + layer.title.toString().replaceAll('"', '""') + '"',
                    '"' + cue.title.toString().replaceAll('"', '""') + '"',
                    ...filteredData
                ].join(',');  
                csv += '\n';
            }  

            const now = new Date();
            const hiddenElement = document.createElement('a');
            const csvData = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
            hiddenElement.href = csvData;
            const filename = this.project.title + '_' + [now.getFullYear(), now.getMonth() + 1, now.getDate()].join('-') + '_' + [now.getHours(), now.getMinutes(), now.getSeconds()].join('-') + '.csv';
            hiddenElement.setAttribute('download', filename);
            document.body.appendChild(hiddenElement);
            hiddenElement.click();
        },
        openPrint(landscape) {
            const {
                width = 700,
                height = 945
            } = this.project.pageSize;
            if (landscape) {
                const routeData = this.$router.resolve({name: 'print', params: {id: this.project.id, layout: 'landscape'}});
                window.open(routeData.href, 'printWindow', `width=${width * 2}, height=${height}`);
            } else {
                const routeData = this.$router.resolve({name: 'print', params: {id: this.project.id, layout: 'portrait'}});
                window.open(routeData.href, 'printWindow', `width=${width}, height=${height}`);
            }
        }
    },
    created: function() {
        NavBus.$on('print-nav.open', () => {
            if (this.$refs.printNav) {
                this.$refs.printNav.open();
            }
        });
    },
    beforeDestroy: function() {
        NavBus.$off('print-nav.open');
    }
};
export default PrintNav;

</script>

<style lang="scss">
.export-nav-wrapper {
    .option-box {
        padding-right: 1rem;
        .option-content {
            margin: 8px 0;
            display: flex;
            flex-wrap: wrap;
            .md-checkbox {
                margin: 4px 0;
                flex: 1 0 50%;
            }
            .option-content-block {
                margin-top: 1rem;
                margin-bottom: 1rem;
            }
            .md-button {
                margin-left: 0;
                margin-right: 0;
            }
        }

        .md-switch {
            &.md-checked {
                .md-switch-container {
                    background-color: darken(#5ecfdf, 30%) !important;

                    .md-switch-thumb {
                        background-color: #5ecfdf !important;
                    }
                }
            }
            label {
                font-weight: 100;
            }
        }
    }
}
.md-flex-90.md-flex-offset-5 {
    margin-right: 5%;
}

.option-note {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.35;
    color: #666;
}
.md-theme-show-mode .option-note {
    color: #aaa;
}
</style>


// WEBPACK FOOTER //
// nav.vue?d380eca6